import { Fragment, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { X } from 'lucide-react'
import { toast } from 'react-toastify'
import { appsAPI } from '../services/api'
import LoadingSpinner from './LoadingSpinner'

function AppModal({ isOpen, onClose, app }) {
  const queryClient = useQueryClient()
  const isEditing = !!app

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm()

  const { data: speechServices, isLoading: servicesLoading } = useQuery(
    'speech-services',
    appsAPI.getSpeechServices,
    { enabled: isOpen }
  )

  const createMutation = useMutation(appsAPI.createApp, {
    onSuccess: () => {
      toast.success('Application created successfully')
      queryClient.invalidateQueries('apps')
      onClose()
      reset()
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create application')
    }
  })

  const updateMutation = useMutation(
    ({ id, data }) => appsAPI.updateApp(id, data),
    {
      onSuccess: () => {
        toast.success('Application updated successfully')
        queryClient.invalidateQueries('apps')
        onClose()
        reset()
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update application')
      }
    }
  )

  useEffect(() => {
    if (app) {
      reset({
        name: app.name,
        description: app.description || '',
        callingWebhook: app.callingWebhook,
        statusWebhook: app.statusWebhook || '',
        speechSynthesisVendor: app.speechSynthesisVendor || '',
        speechRecognitionVendor: app.speechRecognitionVendor || '',
        voice: app.voiceSettings?.voice || '',
        language: app.voiceSettings?.language || 'en-US',
        label: app.voiceSettings?.label || ''
      })
    } else {
      reset({
        name: '',
        description: '',
        callingWebhook: '',
        statusWebhook: '',
        speechSynthesisVendor: '',
        speechRecognitionVendor: '',
        voice: '',
        language: 'en-US',
        label: ''
      })
    }
  }, [app, reset])

  const onSubmit = (data) => {
    const formattedData = {
      ...data,
      voiceSettings: {
        voice: data.voice,
        language: data.language,
        label: data.label
      }
    }

    if (isEditing) {
      updateMutation.mutate({ id: app._id, data: formattedData })
    } else {
      createMutation.mutate(formattedData)
    }
  }

  const isLoading = createMutation.isLoading || updateMutation.isLoading

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-primary-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-primary-400 hover:text-primary-500"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-primary-900">
                      {isEditing ? 'Edit Application' : 'Create New Application'}
                    </Dialog.Title>

                    <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-6">
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium text-primary-700 mb-1">
                            Name *
                          </label>
                          <input
                            {...register('name', { required: 'Name is required' })}
                            type="text"
                            className="input-field"
                            placeholder="Application name"
                          />
                          {errors.name && (
                            <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-primary-700 mb-1">
                            Description
                          </label>
                          <input
                            {...register('description')}
                            type="text"
                            className="input-field"
                            placeholder="Application description"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-primary-700 mb-1">
                          Calling Webhook URL *
                        </label>
                        <input
                          {...register('callingWebhook', { 
                            required: 'Calling webhook is required',
                            pattern: {
                              value: /^https?:\/\/.+/,
                              message: 'Must be a valid URL'
                            }
                          })}
                          type="url"
                          className="input-field"
                          placeholder="https://your-app.com/webhook"
                        />
                        {errors.callingWebhook && (
                          <p className="mt-1 text-sm text-red-600">{errors.callingWebhook.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-primary-700 mb-1">
                          Status Webhook URL
                        </label>
                        <input
                          {...register('statusWebhook', {
                            pattern: {
                              value: /^https?:\/\/.+/,
                              message: 'Must be a valid URL'
                            }
                          })}
                          type="url"
                          className="input-field"
                          placeholder="https://your-app.com/status"
                        />
                        {errors.statusWebhook && (
                          <p className="mt-1 text-sm text-red-600">{errors.statusWebhook.message}</p>
                        )}
                      </div>

                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium text-primary-700 mb-1">
                            Speech Synthesis Vendor
                          </label>
                          {servicesLoading ? (
                            <div className="flex items-center justify-center py-2">
                              <LoadingSpinner size="sm" />
                            </div>
                          ) : (
                            <select
                              {...register('speechSynthesisVendor')}
                              className="input-field"
                            >
                              <option value="">Select TTS vendor</option>
                              {speechServices?.data?.speechServices
                                ?.filter(service => service.vendor === 'aws' || service.vendor === 'google' || service.vendor === 'microsoft')
                                ?.map((service) => (
                                <option key={service.speech_service_sid} value={service.vendor}>
                                  {service.vendor} ({service.name})
                                </option>
                              ))}
                            </select>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-primary-700 mb-1">
                            Speech Recognition Vendor
                          </label>
                          {servicesLoading ? (
                            <div className="flex items-center justify-center py-2">
                              <LoadingSpinner size="sm" />
                            </div>
                          ) : (
                            <select
                              {...register('speechRecognitionVendor')}
                              className="input-field"
                            >
                              <option value="">Select STT vendor</option>
                              {speechServices?.data?.speechServices
                                ?.filter(service => service.vendor === 'aws' || service.vendor === 'google' || service.vendor === 'microsoft')
                                ?.map((service) => (
                                <option key={service.speech_service_sid} value={service.vendor}>
                                  {service.vendor} ({service.name})
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                        <div>
                          <label className="block text-sm font-medium text-primary-700 mb-1">
                            Voice
                          </label>
                          <input
                            {...register('voice')}
                            type="text"
                            className="input-field"
                            placeholder="e.g., Joanna"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-primary-700 mb-1">
                            Language
                          </label>
                          <select
                            {...register('language')}
                            className="input-field"
                          >
                            <option value="en-US">English</option>
                            <option value="ar-SA">Arabic</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-primary-700 mb-1">
                            Label
                          </label>
                          <input
                            {...register('label')}
                            type="text"
                            className="input-field"
                            placeholder="Voice label"
                          />
                        </div>
                      </div>

                      <div className="mt-6 flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={onClose}
                          className="btn-secondary"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isLoading}
                          className="btn-primary"
                        >
                          {isLoading && <LoadingSpinner size="sm" className="mr-2" />}
                          {isLoading ? 'Saving...' : isEditing ? 'Update' : 'Create'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export default AppModal